import React, { useCallback } from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { ImportFileInput, ImportFileOutputData, Tenant } from '#/graphql/types';
import useImportFileUpload from '../../hooks/useImportFileUpload';

export interface SubmitConfirmModalProps {
  visible: boolean;
  uploadedFile: File | null;
  selectedTenant?: Tenant;
  /** Title-case plural entity label used in the modal title, e.g. "Assets" / "Work Orders". */
  entityLabelPlural: string;
  /** Lowercase singular entity label used in the body copy, e.g. "asset" / "work order". */
  entityLabelSingularLower: string;
  generateImportConfig: () => string;
  importFile: (data: ImportFileInput) => Promise<ImportFileOutputData | null>;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

/**
 * Shared "overwrite or skip existing data" confirmation modal for every data-import flow
 * (Asset, Work Order, ...). Uploads the CSV file, calls the entity-specific import mutation
 * and reports back success/failure. Entity-specific import config generation is supplied by
 * the caller.
 */
const SubmitConfirmModal: React.FC<SubmitConfirmModalProps> = ({
  visible,
  uploadedFile,
  selectedTenant,
  entityLabelPlural,
  entityLabelSingularLower,
  generateImportConfig,
  importFile,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { uploadInputFile } = useImportFileUpload();

  const insertNewRecords = useCallback(
    async (skipExisting: boolean) => {
      onHiding();
      onImportStart();

      try {
        // Upload the file to S3 bucket and get the fileId
        const fileId = await uploadInputFile(uploadedFile, selectedTenant?.id);
        if (!fileId) {
          throw new Error('Error uploading file');
        }

        // Call the actual file import on backend side
        const importConfig = generateImportConfig();
        const importResult = await importFile({
          importConfig,
          fileId,
          skipExisting,
        });

        if (!importResult) {
          throw new Error('Import failed');
        }

        onSuccess(importResult);
      } catch (error) {
        onFailed(error);
        return;
      }
    },
    [
      uploadInputFile,
      uploadedFile,
      selectedTenant,
      generateImportConfig,
      importFile,
      onHiding,
      onImportStart,
      onSuccess,
      onFailed,
    ],
  );

  const handleOverwriteSkip = useCallback(async (): Promise<void> => {
    await insertNewRecords(true);
  }, [insertNewRecords]);

  const handleOverwriteConfirm = useCallback(async (): Promise<void> => {
    await insertNewRecords(false);
  }, [insertNewRecords]);

  return (
    <Modal
      visible={visible}
      title={`Overwrite Data of Existing ${entityLabelPlural}`}
      width={640}
      height="auto"
      onHiding={onHiding}
      content={
        <>
          <div>
            If there are already existing {entityLabelSingularLower} records do you want to
            overwrite them with the new data from this file or skip them?
          </div>
          <div>Overwriting the records means that all changes made before will be lost.</div>
        </>
      }
      actions={[
        <TMMButton key="cancel" label="Skip" type="normal" onClick={handleOverwriteSkip} />,
        <TMMButton
          key="ok"
          label="Overwrite Data"
          type="submit"
          onClick={handleOverwriteConfirm}
        />,
      ]}
    />
  );
};

export default React.memo(SubmitConfirmModal);
